/* eslint-disable indent,no-new,no-undef,no-unused-expressions, no-useless-escape */
export default {
  data () {
    return {
      sidebar: [
        {name: 'Introduction', url: '/docs/api/v5'},
        {name: 'Authentication', url: '/docs/api/v5/authentication'},
        {name: 'Pagination', url: '/docs/api/v5/pagination'},
        {
          name: [
            {name: 'Resources', main: true},
            {name: 'Reviews', url: '/docs/api/v5/reviews'},
            {name: 'Replies', url: '/docs/api/v5/replies'}
          ]
        }
      ],

      // Make a key related to Route name you provide above and assign the same key value like below, just change the content
      introduction: {
        codeSection: {
          styles: [
            {keyword: 'import', className: 'import'}
          ]
        },
        mainSection: {
          apiReferance: {
            title: 'API Reference - Introduction',
            description: `
            The Hellopeter Business API is structured using standard HTTP <strong>RESTful</strong> principles based on predictable resource-oriented URLs. It accepts <strong>form-encoded</strong> request payloads and returns <strong>JSON-encoded</strong> responses, uses standard HTTP response codes and verbs.
            <br><br>
            The API uses standard error codes consistent with <strong>REST</strong> so that you can programmatically react to our API responses, for example - if you make a call to the <strong>/reviews</strong> resource and pass in a permalink, when you receive an HTTP status code of 404, it means that either the permalink is incorrect OR that the review was subsequently removed from our platform.
            <br><br>
            Over time, we will make more resources available via our API making it even easier for your business to fully integrate our platform into your <strong>CRM</strong>
            <br><br>
            Our intention is to make the full <strong>review management process</strong> that you're familiar with in our Business Portal, as accessible from our API.
          `,
            code: `
            https://api.hellopeter.com/v5/api
          `,
            detail: [
              {title: 'Accessing our API', summary: 'Our API is only accessible to businesses who have a valid API Key.'}
            ],
            blockHeader: 'Base URL'
          },
          summarySection: null,
          responseSection: null
        }
      },
      authentication: {
        codeSection: {
          styles: [
            {keyword: 'curl', className: 'import'},
            {keyword: 'GET', className: 'import'},
            {keyword: '--header', className: 'import'},
            {keyword: '12345', className: 'import'}
          ]
        },
        mainSection: {
          apiReferance: {
            title: 'Authentication',
            description: `
            Every API call requires authentication using a unique API Key that you can manage from the Hellopeter Business Portal. The very first time you use the API, you would need to generate a key. To do this, once logged into your business profile, navigate to the <strong>Integrations</strong>&nbsp;&rarr;&nbsp;<strong>API</strong>&nbsp;menu-item and click on the <strong>GENERATE&nbsp;NEW&nbsp;KEY</strong>&nbsp;button.
            <br><br>
            If you already have an API Key and you need to change it, simply click the <strong>REGENERATE&nbsp;KEY</strong> button. This is especially useful if you know or suspect that your API Key has been compromised.
          `,
            code: `
            curl --location --request GET 'https://api.hellopeter.com/v5/api/reviews' \\<br/>
            &nbsp;&nbsp;--header 'Accept: application/vnd.api+json' \\<br/>
            &nbsp;&nbsp;--header 'Content-Type: application/json' \\<br/>
            &nbsp;&nbsp;--header 'apiKey: 12345'
          `,
            detail: [
              {title: 'Where to pass the API Key?', summary: `The API Key needs to be set as an HTTP Header`}
            ],
            blockHeader: 'Example using the apiKey field'
          },
          summarySection: null,
          responseSection: null
        }
      },
      pagination: {
        codeSection: {
          styles: [
            {keyword: 'import', className: 'import'}
          ]
        },
        mainSection: {
          apiReferance: {
            title: 'Pagination',
            description: `
            Pagination meta data is available on successful <strong>GET</strong> requests to <strong>list</strong> resources i.e when making a successful GET request to a resource <strong>without</strong> passing a unique identifier to the URL as a parameter and this will return two objects:
            <br><br>
            1. meta - the meta data related to pagination<br>
            2. data - an array of objects based on the resource requested
            <br><br>
            The meta data contains all the values commonly associated with RESTful pagination so that you can programmatically navigate the entire list of the returned resource objects
          `,
            code: null,
            detail: null,
            blockHeader: null
          },
          summarySection: {
            heading: 'Pagination - Meta Data',
            subheading: 'Fields returned for the meta section',
            summary: null,
            detail: [
              {title: '<b>totalRecords</b>', description: `The total number of records found in the requested resource given the supplied parameters`},
              {title: '<b>perPage</b>', description: `The maximum number of returned objects for the requested resource - default value is 10`},
              {title: '<b>previousPage</b>', description: `The absolute URL to get the previous page of results`},
              {title: '<b>current</b>', description: `An integer representing the current page number or position in the pagination list`},
              {title: '<b>nextPage</b>', description: `The absolute URL to get the next page of results`},
              {title: '<b>lastPage</b>', description: `An integer representing the last page number or position in the pagination list`},
              {title: '<b>from</b>', description: `An integer representing the start position of the entry from the list of results`},
              {title: '<b>to</b>', description: `An integer representing the end position of the entry the list of results`}
            ]
          },
          responseSection: {
            heading: null,
            description: `
            An example of the meta response object
          `,
            response: {
              data: [],
              meta: {
                totalRecords: '12',
                perPage: '10',
                previousPage: 'null',
                current: '1',
                nextPage: 'https://api.hellopeter.com/v5/api/reviews?page=2',
                lastPage: '2',
                from: '1',
                to: '10'
              }
            }
          }
        }
      },
      reviews: {
        codeSection: {
          styles: [
            {keyword: 'curl', className: 'import'},
            {keyword: 'GET', className: 'import'},
            {keyword: '--header', className: 'import'},
            {keyword: '12345', className: 'import'}
          ]
        },
        mainSection: {
          apiReferance: {
            title: 'Reviews',
            description: `
            The <strong>reviews</strong> resource only allows GET requests to be performed. One can perform an HTTP GET request to retrieve a paginated list of reviews or one could request the detail for a single review
            <br><br>
            When requesting to get the detail for a single review, you have to pass in the review permalink as a parameter to the reviews resource
            <br><br>
            Please note that every API call to get either a single review resource or a list of reviews results in the <strong>consumption of review credits</strong> when applicable. This only applies to reviews for which you have not yet received <strong>Customer Data</strong>. Also, when fetching a review that was converted from a <strong>Review Invite</strong>, that customer data is instead consumed from the <strong>Review Invite Credit</strong>
            <br><br>
            In the event that you have insufficient <strong>Review Credits</strong>, when making a GET request to return new reviews for which you have not yet read or received customer data, the object containing the customer data will be empty and contain an error message saying something along the lines of <strong>insufficient credits available</strong>
          `,
            code: `
            curl --location --request GET '/reviews/an-example-review-56789' \\<br/>
            &nbsp;&nbsp;--header 'Accept: application/vnd.api+json' \\<br/>
            &nbsp;&nbsp;--header 'Content-Type: application/json' \\<br/>
            &nbsp;&nbsp;--header 'apiKey: 12345'
          `,
            detail: [
              {title: 'How to get a single review?', summary: `You need to pass in the permalink of the review`}
            ],
            blockHeader: 'Example requesting a single review'
          },
          summarySection: {
            heading: 'Response Fields',
            subheading: null,
            summary: `
            Fetching a single review will have some additional fields compared to fetching a list of reviews. When fetching a single review, you would also get detail for internal notes, branches, tags and the replies associated to the review
          `,
            detail: [
              {title: '<b>permalink</b', description: `The unique permalink used to identify the review`},
              {title: '<b>user</b', description: `The Display name of the reviewer`},
              {title: '<b>rating</b', description: `The Star Rating of the review`},
              {title: '<b>nps</b', description: `The Net Promoter Score category`},
              {title: '<b>npsScore</b', description: `An integer value ranging from 0 to 10 for teh Net Promoter Score`},
              {title: '<b>channel</b', description: `The Channel medium of which the review was published - either HELLOPETER, FACEBOOK or GOOGLE`},
              {title: '<b>status</b', description: `The status of the review which could be one of the following - UNREAD, REPLIED, UNREPLIED, UNREPLIED_COMMENT, REPORTED or ERROR`},
              {title: '<b>title</b', description: `The title of the Review`},
              {title: '<b>body</b', description: `The body of the Review`},
              {title: '<b>responseReceived</b', description: `A boolean value indicating whether or not a response was received`},
              {title: '<b>responseTimestamp</b', description: `A timestamp of the very first reply to this review`},
              {title: '<b>timeToRespond</b', description: `An integer representing the number of minutes that has lapsed between when the review was published and when the first reply was written by the business`},
              {title: '<b>createdAt</b', description: `The timestamp for when the review was created`},
              {title: '<b>images</b', description: `An array of URLs containing any uploaded images as part of the review`},
              {title: '<b>customerData</b', description: `Customer Data, this object will contain the name, phone, email, ID Number and reference number of the customer`},
              {title: '<b>notes</b', description: `Any internal notes left by business users`},
              {title: '<b>replies</b', description: `All replies, both from the consumer and the business, belonging to the review`},
              {title: '<b>branches</b', description: `The branches associated with the review`},
              {title: '<b>tags</b', description: `All tags added to the review`}
            ]
          },
          responseSection: {
            heading: 'Please note',
            description: `
            Review credits are consumed with every review that is successfully retrieved via the API. A review credit will not be consumed when one has already been used for the review OR whent he review is a conversion from a review invite
          `,
            response: {
              'permalink': '3-star-baddf43947cac263f2ea4923135fb554ba648880-3098948',
              'user': 'Batman G',
              'rating': '3',
              'nps': 'null',
              'npsScore': 'null',
              'channel': 'HELLOPETER',
              'status': 'REPLIED',
              'title': '3 star',
              'body': '3 star',
              'responseReceived': 'true',
              'responseTimestamp': '2021-12-09 10:06:29',
              'timeToRespond': '2',
              'created_at': '2021-12-09T08:03:50.000000Z',
              'images': [],
              'customerData': {
                'name': 'Batman G',
                'phone': '0823333333',
                'email': 'bat@man.com',
                'idNumber': '8001232323232',
                'refNumber': 'Not provided',
                'error': 'null'
              },
              'notes': [],
              'replies': [
                {
                  'private': 'false',
                  'created_at': '2021-12-09 10:06:29',
                  'edited_at': '2021-12-09 10:06:29',
                  'id': '12345',
                  'body': 'this is a another private reply',
                  'user_id': '12345',
                  'first_name': 'Business-API',
                  'last_name': 'Plug nation',
                  'from_business': 'true'
                }
              ],
              'branches': [],
              'tags': []
            }
          }
        }
      },
      replies: {
        codeSection: {
          styles: [
            {keyword: 'curl', className: 'import'},
            {keyword: 'GET', className: 'import'},
            {keyword: 'POST', className: 'import'},
            {keyword: 'PUT', className: 'import'},
            {keyword: 'DELETE', className: 'import'},
            {keyword: '--header', className: 'import'},
            {keyword: '12345', className: 'import'},
            {keyword: 'FETCHING', className: 'import'},
            {keyword: 'CREATING', className: 'import'},
            {keyword: 'EDITING', className: 'import'},
            {keyword: 'DELETING', className: 'import'},
            {keyword: '--data-raw', className: 'import'}
          ]
        },
        mainSection: {
          apiReferance: {
            title: 'Review Replies',
            description: `
            The <strong>replies</strong> resource is a <strong>sub-resource</strong> of Reviews i.e to perform any HTTP action relating to replies, you would need to supply the review permalink as a parameter for the reply intent - whether that is to create, update or delete a reply.
            <br><br>
            When requesting to get the detail for a single review, you have to pass in the review permalink as a parameter to the reviews resource
          `,
            code: `
            FETCHING<br>
            curl --location --request GET '/reviews/an-example-review-56789/replies' \\<br/>
            &nbsp;&nbsp;--header 'Accept: application/vnd.api+json' \\<br/>
            &nbsp;&nbsp;--header 'Content-Type: application/json' \\<br/>
            &nbsp;&nbsp;--header 'apiKey: 12345'<br/><br/>
            CREATING<br>
            curl --location --request POST '/reviews/fwefwe-3098949/replies' \\<br/>
            &nbsp;&nbsp;--header 'Accept: application/json' \\<br/>
            &nbsp;&nbsp;--header 'Content-Type: application/json' \\<br/>
            &nbsp;&nbsp;--header 'apiKey: 12345' \\<br/>
            &nbsp;&nbsp;--data-raw '{"reply": { "body": "this is a test public reply", "private" : false }}'<br/><br/>
            EDITING<br>
            curl --location --request PUT '/reviews/fwefwe-3098949/replies/12345' \\<br/>
            &nbsp;&nbsp;--header 'Accept: application/json' \\<br/>
            &nbsp;&nbsp;--header 'Content-Type: application/json' \\<br/>
            &nbsp;&nbsp;--header 'apiKey: 12345' \\<br/>
            &nbsp;&nbsp;--data-raw '{"reply": { "body": "this is a test public reply", "private" : false }}'<br/><br/>
            DELETING<br>
            curl --location --request DELETE '/reviews/fwefwe-3098949/replies/12345' \\<br/>
            &nbsp;&nbsp;--header 'Accept: application/json' \\<br/>
            &nbsp;&nbsp;--header 'Content-Type: application/json' \\<br/>
            &nbsp;&nbsp;--header 'apiKey: 12345'
          `,
            detail: [
              {title: 'How to get replies for a single review?', summary: `You need to pass the replies resource after the permalink of the review`},
              {title: 'How to create a Reply?', summary: `You need to perform an HTTP POST to the /replies resource and pass a payload that contains a 'reply' object, which in turn has a boolean field to mark it is 'private' and a 'body' field with the reply content`},
              {title: 'How to edit a Reply?', summary: `You need to perform an HTTP PUT to the /replies/12345 resource where 12345 represents the ReplyID and pass a payload that contains a 'reply' object, which in turn has a boolean field to mark it is 'private' and a 'body' field with the reply content`},
              {title: 'How to delete a Reply?', summary: `You need to perform an HTTP DELETE to the /replies/12345 resource where 12345 represents the ReplyID. No payload is required`}
            ],
            blockHeader: 'Examples fetching and creating replies'
          },
          summarySection: {
            heading: 'Response fields',
            subheading: null,
            summary: null,
            detail: [
              {title: '<b>private</b>', description: `A boolean indicating wether the reply is public or private`},
              {title: '<b>edited_at</b>', description: `A timestamp showing when the reply was last edited`},
              {title: '<b>created_at</b>', description: `A timestamp showing when the reply was created`},
              {title: '<b>id</b>', description: `The ID of the reply`},
              {title: '<b>user_id</b>', description: `The user ID of the user who created the reply`},
              {title: '<b>body</b>', description: `The body of the reply`},
              {title: '<b>first_name</b>', description: `The first name of the user who created the reply`},
              {title: '<b>last_name</b>', description: `The last name of the user who created the reply`},
              {title: '<b>from_business</b>', description: `A boolean indicating whether the reply was from your business OR from the reviewer`}
            ]
          },
          responseSection: null
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/documentation.js